import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, Download } from "lucide-react";
import { readJwt } from "../auth/storage";
import {
  api,
  type LogLevel,
  type LogLineDto,
  type LogsQuery,
  type StreamQualityResponse,
} from "../api/client";
import LogTail from "../components/logs/LogTail";
import StreamQuality from "../components/analytics/StreamQuality";
import AppShell from "../components/layout/AppShell";
import PageOwner from "../components/common/PageOwner";
import Panel from "../components/common/Panel";
import { Button } from "@/components/ui/button";

/**
 * SPEC §14 Journal — tail live avec filtres sévérité / composant / recherche,
 * pause, téléchargement, + la qualité du flux IG 7 j (diagnostic infra — sa
 * maison est ici, pas sur l'Analytique). Poll GET /api/logs toutes les 1,5 s en
 * incrémental (since=lastTimeMs). Sur échec de poll : erreur explicite, le tail
 * cesse d'avancer (B5b).
 */
const POLL_INTERVAL_MS = 1500;
const MAX_LINES = 2000;
const CONTROL = "field";

export default function LogsPage() {
  const [filters, setFilters] = useState<LogsQuery>({});
  const [lines, setLines] = useState<LogLineDto[]>([]);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const lastTimeRef = useRef<number>(0);

  const [streamQuality, setStreamQuality] = useState<StreamQualityResponse | null>(null);
  const [sqLoading, setSqLoading] = useState(true);
  const [sqError, setSqError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .streamQuality(168)
      .then((res) => {
        if (cancelled) return;
        setStreamQuality(res);
        setSqLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSqError(true);
        setSqLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    lastTimeRef.current = 0;
    setLines([]);
    setError(false);
  }, [filtersKey]);

  useEffect(() => {
    if (paused) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.logs({ ...filters, since: lastTimeRef.current });
        if (cancelled) return;
        if (res.lines.length > 0) {
          setLines((prev) => {
            const next = prev.concat(res.lines);
            return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
          });
          lastTimeRef.current = res.lastTimeMs;
        }
        setError(false);
      } catch {
        if (!cancelled) setError(true);
      }
    };
    void tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [paused, filtersKey, filters]);

  const updateFilter = useCallback(
    <K extends keyof LogsQuery>(key: K, value: LogsQuery[K] | undefined) => {
      setFilters((prev) => {
        const next: LogsQuery = { ...prev };
        if (value == null || value === "") {
          delete next[key];
        } else {
          next[key] = value;
        }
        return next;
      });
    },
    [],
  );

  const download = async () => {
    setDownloadError(null);
    try {
      const url = api.logsDownloadUrl(filters);
      const jwt = readJwt();
      const res = await fetch(url, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      if (!res.ok) {
        setDownloadError(`Téléchargement échoué : HTTP ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `dax-bot-logs-${Date.now()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Téléchargement échoué");
    }
  };

  return (
    <AppShell>
      <div className="stagger flex flex-col gap-4">
        {/* Variante B (2026-07-31) : une seule app, un seul backend — les actions
            des deux cockpits sortent dans le même journal. */}
        <PageOwner variant="common" />
        <Panel
          title="Filtres"
          aria-label="Filtres"
          aside={
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setPaused((p) => !p)}
                data-testid="pause-toggle"
              >
                {paused ? <Play className="size-3.5" /> : <Pause className="size-3.5" />}
                {paused ? "Reprendre" : "Pause"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={download}
                data-testid="logs-download-button"
              >
                <Download className="size-3.5" />
                Télécharger
              </Button>
            </div>
          }
          bodyClassName="flex flex-col gap-3"
        >
          <div className="flex flex-wrap items-center gap-4">
            <label className="text-muted-foreground flex items-center gap-2 text-sm">
              Niveau
              <select
                className={CONTROL}
                data-testid="filter-level"
                value={filters.level ?? ""}
                onChange={(e) =>
                  updateFilter("level", (e.target.value as LogLevel) || undefined)
                }
              >
                <option value="">Tous</option>
                <option value="DEBUG">DEBUG+</option>
                <option value="INFO">INFO+</option>
                <option value="WARN">WARN+</option>
                <option value="ERROR">ERROR</option>
              </select>
            </label>
            <label className="text-muted-foreground flex items-center gap-2 text-sm">
              Composant
              <input
                type="text"
                className={CONTROL}
                data-testid="filter-component"
                value={filters.component ?? ""}
                placeholder="ex. algo, ig, runtime"
                onChange={(e) => updateFilter("component", e.target.value || undefined)}
              />
            </label>
            <label className="text-muted-foreground flex items-center gap-2 text-sm">
              Recherche
              <input
                type="text"
                className={CONTROL}
                data-testid="filter-search"
                value={filters.search ?? ""}
                placeholder="fragment du message"
                onChange={(e) => updateFilter("search", e.target.value || undefined)}
              />
            </label>
          </div>
          {error && (
            <p className="text-loss text-sm" role="alert" data-testid="logs-error">
              <code>GET /api/logs</code> injoignable — le tail est périmé. Nouvel
              essai toutes les {POLL_INTERVAL_MS} ms.
            </p>
          )}
          {downloadError && (
            <p
              className="text-muted-foreground text-sm"
              role="alert"
              data-testid="logs-download-error"
            >
              {downloadError}
            </p>
          )}
        </Panel>

        <Panel
          title="Tail"
          aria-label="Tail live"
          aside={
            <span data-testid="tail-status">
              {paused ? "En pause" : "Live"} — {lines.length} ligne
              {lines.length === 1 ? "" : "s"}
            </span>
          }
          bodyClassName="p-0"
        >
          <LogTail lines={lines} autoScroll={!paused} />
        </Panel>

        <Panel title="Qualité du flux (7 jours)" aria-label="Qualité du flux">
          <StreamQuality data={streamQuality} loading={sqLoading} error={sqError} />
        </Panel>
      </div>
    </AppShell>
  );
}
